import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

type FeedbackDialogProps = {
  open: boolean;
  onClose: () => void;
  onSendFeedback: (comment: string) => Promise<boolean>;
};

export const FeedbackDialog = ({ open, onClose, onSendFeedback }: FeedbackDialogProps) => {
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFeedbackComment('');
    setFeedbackSent(false);
    setFeedbackError(null);
    setIsSendingFeedback(false);
  }, [open]);

  const handleSendFeedback = async () => {
    if (isSendingFeedback) {
      return;
    }

    setIsSendingFeedback(true);
    setFeedbackError(null);

    try {
      const didSend = await onSendFeedback(feedbackComment);
      if (didSend) {
        setFeedbackSent(true);
        setFeedbackComment('');
      } else {
        setFeedbackError('Unable to send feedback. Please try again.');
      }
    } finally {
      setIsSendingFeedback(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isSendingFeedback && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Feedback</DialogTitle>
      <DialogContent>
        {feedbackSent ? (
          <Box sx={{ py: 1 }}>Thank you! Your feedback is really appreciated.</Box>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body1">
              Your comments and suggestions will be used to make dotstorming better!
            </Typography>
            <TextField
              multiline
              minRows={5}
              label="Please enter your comment"
              value={feedbackComment}
              onChange={(event) => setFeedbackComment(event.target.value)}
              disabled={isSendingFeedback}
              fullWidth
            />
            {feedbackError ? <Alert severity="error">{feedbackError}</Alert> : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {feedbackSent ? (
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={isSendingFeedback}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSendFeedback()}
              variant="contained"
              disabled={isSendingFeedback}
            >
              Send
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
